import { useEffect, useState, useCallback } from "react";
import axios from "axios";

// Fetch/cache the /api/settings/public payload. Cached in localStorage for
// instant reads on Header/Footer without a flash of default content.
const BASE = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "dh_public_settings_v1";

let inflight = null;

export const loadPublicSettings = async ({ force = false } = {}) => {
  if (!force) {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached && cached._t && Date.now() - cached._t < 5 * 60 * 1000) {
        return cached.data;
      }
    } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/settings/public`);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ _t: Date.now(), data }));
      return data;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

export const usePublicSettings = () => {
  const [settings, setSettings] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      return cached?.data || null;
    } catch (e) { if (typeof console !== "undefined") console.debug("[silent]", e); return null; }
  });

  const refresh = useCallback(async () => {
    const data = await loadPublicSettings({ force: true });
    setSettings(data);
    return data;
  }, []);

  useEffect(() => {
    let alive = true;
    loadPublicSettings().then((d) => { if (alive) setSettings(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return { settings, refresh };
};

export const clearPublicSettingsCache = () => localStorage.removeItem(CACHE_KEY);
